import { fadeIn, fadeOut } from './helpers';
const FOCUSABLE_SELECTOR = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';
class Popup {
    constructor(el) {
        this.lastFocused = null;
        this.isOpen = false;
        this.popup = el;
        this.content = el.querySelector('.popup__content');
        this.closeButtons = Array.from(el.querySelectorAll('[data-role="close-popup"]'));
        this.bindEvents();
    }
    open() {
        if (this.isOpen)
            return;
        this.isOpen = true;
        const doc = this.popup.ownerDocument;
        this.lastFocused = doc.activeElement;
        this.popup.setAttribute('aria-hidden', 'false');
        this.popup.removeAttribute('hidden');
        this.popup.dataset.state = 'open';
        doc.body.classList.add('overflow-hidden');
        fadeIn(this.popup, 'flex');
        requestAnimationFrame(() => {
            (this.getFocusables().first ?? this.content ?? this.popup).focus({ preventScroll: true });
        });
        this.setOpeners('true');
    }
    async close() {
        if (!this.isOpen)
            return;
        this.isOpen = false;
        const doc = this.popup.ownerDocument;
        doc.body.classList.remove('overflow');
        this.popup.dataset.state = '';
        this.setOpeners('false');
        this.restoreFocus();
        await fadeOut(this.popup);
        this.popup.setAttribute('aria-hidden', 'true');
        this.popup.setAttribute('hidden', '');
        doc.body.classList.remove('overflow-hidden');
    }
    bindEvents() {
        this.closeButtons.forEach((btn) => {
            btn.addEventListener('click', (e) => { e.preventDefault(); this.close(); });
        });
        const onOutside = (ev) => {
            if (!this.isOpen)
                return;
            if (this.popup.getAttribute('data-popup-static') === 'true')
                return;
            const t = ev.target;
            if (this.popup.contains(t) && !this.content?.contains(t))
                this.close();
        };
        this.popup.addEventListener('mousedown', onOutside, true);
        this.popup.addEventListener('touchstart', onOutside, { capture: true, passive: true });
    }
    getFocusables() {
        const nodes = Array.from(this.popup.querySelectorAll(FOCUSABLE_SELECTOR)).filter((el) => !el.hasAttribute('disabled') && el.tabIndex !== -1 &&
            !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length));
        return { first: nodes[0] ?? null, last: nodes[nodes.length - 1] ?? null };
    }
    restoreFocus() {
        const doc = this.popup.ownerDocument;
        const active = doc.activeElement;
        if (active && this.popup.contains(active)) {
            active.blur();
        }
        const opener = this.popup.id
            ? doc.querySelector(`[aria-controls="${this.popup.id}"]`)
            : null;
        const next = (this.lastFocused && !this.popup.contains(this.lastFocused))
            ? this.lastFocused
            : opener ?? doc.body;
        if (active && this.popup.contains(active)) {
            next.focus?.({ preventScroll: true });
        }
    }
    setOpeners(expanded) {
        if (!this.popup.id)
            return;
        this.popup.ownerDocument
            .querySelectorAll(`[aria-controls="${this.popup.id}"]`)
            .forEach((btn) => btn.setAttribute('aria-expanded', expanded));
    }
    static get(el) {
        let inst = Popup.instances.get(el);
        if (!inst) {
            inst = new Popup(el);
            Popup.instances.set(el, inst);
        }
        return inst;
    }
    static initAll() {
        const doc = window.document;
        doc.querySelectorAll('.popup').forEach((el) => Popup.get(el));
        doc.addEventListener('click', (e) => {
            const opener = e.target.closest('[data-popup-open]');
            if (!opener)
                return;
            e.preventDefault();
            const raw = opener.getAttribute('data-popup-open') ?? '';
            const id = raw.startsWith('#') ? raw.slice(1) : raw;
            if (!id)
                return;
            const el = doc.getElementById(id);
            if (!el)
                return;
            const inst = Popup.get(el);
            opener.setAttribute('aria-controls', id);
            opener.setAttribute('aria-expanded', inst.isOpen ? 'true' : 'false');
            inst.open();
        });
        doc.addEventListener('keydown', (e) => {
            const openEl = doc.querySelector('.popup[data-state="open"]');
            if (!openEl)
                return;
            if (e.key === 'Escape') {
                Popup.get(openEl).close();
                return;
            }
            if (e.key === 'Tab') {
                const { first, last } = Popup.get(openEl).getFocusables();
                if (!first || !last)
                    return;
                const active = openEl.ownerDocument.activeElement;
                if (e.shiftKey && active === first) {
                    e.preventDefault();
                    last.focus({ preventScroll: true });
                }
                else if (!e.shiftKey && active === last) {
                    e.preventDefault();
                    first.focus({ preventScroll: true });
                }
            }
        }, true);
    }
}
Popup.instances = new WeakMap();
export const initPopups = () => {
    Popup.initAll();
};
export default initPopups;
